import React from 'react'
import { Alert, Collapse } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import { getDetails, getQuoteOptions } from '@paintscout/util/builder'
import classNames from 'classnames'
import AdditionalWorkSection from './AdditionalWorkSection'
import ContactCard from './ContactCard'
import SummaryToggleRow from './SummaryToggleRow'
import WorkDetails from './WorkDetails'
import { getPainterEventAddress } from './eventUtils'
import type { PainterScheduleEvent } from './types'
import type { usePainterAdditionalWorkStyles } from './usePainterAdditionalWorkStyles'
import type { usePainterWorkOrderStyles } from './usePainterWorkOrderStyles'
import type { OptionsDocument } from 'paintscout'
import striptags from 'striptags'

interface WorkOrderContentProps {
  event: PainterScheduleEvent
  showContactInfo: boolean
  showHours: boolean
  canAddAdditionalWork: boolean
  crewNoteExpanded: boolean
  productsExpanded: boolean
  visibleAdditionalWork: PainterScheduleEvent['additionalWork']
  onToggleCrewNote: () => void
  onToggleProducts: () => void
  onAddAdditionalWork: () => void
  workClasses: ReturnType<typeof usePainterWorkOrderStyles>
  additionalWorkClasses: ReturnType<typeof usePainterAdditionalWorkStyles>
  callbacks: AdditionalWorkCallbacks
}

interface AdditionalWorkCallbacks {
  onAcceptSign?: (id: string) => void
  onEdit?: (id: string) => void
  onDelete?: (id: string) => void
  onViewMedia?: (id: string) => void
}

export default function WorkOrderContent(props: WorkOrderContentProps) {
  const {
    event,
    showContactInfo,
    showHours,
    canAddAdditionalWork,
    crewNoteExpanded,
    productsExpanded,
    visibleAdditionalWork = [],
    onToggleCrewNote,
    onToggleProducts,
    onAddAdditionalWork,
    workClasses,
    additionalWorkClasses,
    callbacks
  } = props
  const { options } = useClientOptions()
  const address = getPainterEventAddress(event)
  const sourceQuote = event.sourceQuote
  const quoteOptions =
    sourceQuote && options ? getQuoteOptions({ quote: sourceQuote, options: options as OptionsDocument }) : null
  const allowShowHours =
    !quoteOptions ||
    !sourceQuote ||
    !options ||
    !getDetails({ quote: sourceQuote, options: options as OptionsDocument }).totals.hours.useCustom ||
    quoteOptions.allowShowCustomizedTotalHours
  const showItemHours = showHours && (quoteOptions?.showWorkOrderAreaValues ?? true)
  const showTotalHours = showHours && (quoteOptions?.showWorkOrderTotalHours ?? true)
  return (
    <>
      {(event.contact || address) && (
        <ContactCard event={event} showClientInfo={showContactInfo} classes={workClasses} />
      )}
      {!event.sourceQuote && (
        <Alert severity="warning" className={workClasses.noWorkOrderBanner}>
          No invoice work order found for this job. Contact your lead to get started.
        </Alert>
      )}
      {showTotalHours && event.totalHours != null && !!event.sourceQuote && (
        <div className={classNames(workClasses.summaryRow, workClasses.summaryRowStatic)}>
          <span className={workClasses.summaryRowLabel}>Total Hours:</span>
          <span className={workClasses.summaryRowValueText}>{event.totalHours}</span>
        </div>
      )}
      {event.crewNote && !!striptags(event.crewNote) && (
        <>
          <SummaryToggleRow
            label="Crew Note"
            expanded={crewNoteExpanded}
            onToggle={onToggleCrewNote}
            classes={workClasses}
          />
          <Collapse show={crewNoteExpanded}>
            <div className={workClasses.summaryRowContent} dangerouslySetInnerHTML={{ __html: event.crewNote }} />
          </Collapse>
        </>
      )}
      {event.productString && (
        <>
          <SummaryToggleRow
            label="Products"
            expanded={productsExpanded}
            onToggle={onToggleProducts}
            classes={workClasses}
          />
          <Collapse show={productsExpanded}>
            <div className={workClasses.summaryRowContent} dangerouslySetInnerHTML={{ __html: event.productString }} />
          </Collapse>
        </>
      )}
      <WorkDetails sourceQuote={event.sourceQuote} showHours={showItemHours} classes={workClasses} />
      <AdditionalWorkSection
        items={visibleAdditionalWork}
        showHours={showItemHours && allowShowHours}
        canAddAdditionalWork={canAddAdditionalWork}
        onAdd={onAddAdditionalWork}
        classes={additionalWorkClasses}
        {...callbacks}
      />
    </>
  )
}
